import { idToken } from "../firebase";

/** The shape every Shoku API uses for a failure. */
export interface ApiErrorBody {
  error?: string;
  code?: string;
  message?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: ApiErrorBody | null;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.message || body?.error || fallback);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.code ?? "";
    this.body = body;
  }

  /** The contractor is verified but has been blocked from taking new work. */
  get isBlocked(): boolean {
    return this.code === "CONTRACTOR_BLOCKED";
  }

  /** Quoting and accepting bookings both require a payout account first. */
  get needsPayoutSetup(): boolean {
    return this.code === "PAYOUT_SETUP_REQUIRED";
  }

  /** The account is still awaiting admin verification. */
  get isUnverified(): boolean {
    return this.code === "NOT_VERIFIED";
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("We couldn't reach Shoku. Check your connection and try again.");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Skip the Authorization header entirely (nothing needs this yet). */
  anonymous?: boolean;
}

function buildUrl(base: string, path: string, query?: RequestOptions["query"]): string {
  const url = new URL(path, base);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function parseBody(res: Response): Promise<unknown> {
  // 204 is the documented success shape for DELETE /profile/services/:id,
  // POST /bookings/:id/decline and the FCM device routes.
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/**
 * A JSON client for one Shoku API origin.
 *
 * Two behaviours here are load-bearing and were both learned the hard way:
 *
 *  1. `credentials: "omit"`. The APIs answer `Access-Control-Allow-Origin` for
 *     an allowlisted origin and deliberately never send `Allow-Credentials`; a
 *     credentialed request would be rejected by CORS before it arrived. Auth is
 *     a bearer token, never a cookie.
 *
 *  2. Retry on **403 as well as 401**. The mobile interceptor retries 401 only,
 *     which is exactly why issue #74 happened: after POST /auth/complete mints
 *     the `userType` claim, the cached ID token still lacks it and the next call
 *     answers 403 — a status the client could never recover from. One forced
 *     refresh, one retry, then give up.
 */
export class ApiClient {
  constructor(private readonly baseUrl: string) {}

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = "GET", body, query, signal, anonymous = false } = options;
    const url = buildUrl(this.baseUrl, path, query);

    const send = async (forceRefresh: boolean): Promise<Response> => {
      const headers: Record<string, string> = { Accept: "application/json" };
      if (body !== undefined) headers["Content-Type"] = "application/json";
      if (!anonymous) {
        const token = await idToken(forceRefresh);
        if (token) headers["Authorization"] = `Bearer ${token}`;
      }

      try {
        return await fetch(url, {
          method,
          headers,
          credentials: "omit",
          ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
          ...(signal ? { signal } : {}),
        });
      } catch (cause) {
        if (signal?.aborted) throw cause;
        throw new NetworkError(cause);
      }
    };

    let res = await send(false);

    if ((res.status === 401 || res.status === 403) && !anonymous) {
      res = await send(true);
    }

    const parsed = await parseBody(res);

    if (!res.ok) {
      throw new ApiError(res.status, parsed as ApiErrorBody | null, `Request failed (${res.status})`);
    }

    return parsed as T;
  }

  get<T>(path: string, options?: Omit<RequestOptions, "method" | "body">): Promise<T> {
    return this.request<T>(path, { ...options, method: "GET" });
  }

  post<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">): Promise<T> {
    return this.request<T>(path, { ...options, method: "POST", body });
  }

  patch<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">): Promise<T> {
    return this.request<T>(path, { ...options, method: "PATCH", body });
  }

  put<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">): Promise<T> {
    return this.request<T>(path, { ...options, method: "PUT", body });
  }

  delete<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">): Promise<T> {
    return this.request<T>(path, { ...options, method: "DELETE", body });
  }
}
